import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  BehaviorSubject,
  forkJoin,
  Subject,
  Observable,
  of
} from 'rxjs';
import {
  takeUntil,
  switchMap,
  distinctUntilChanged,
} from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import {
  EmptyURIValue,
  encodeSpecialCharacters,
  URIPrefix,
  UriDelimiter,
  SESSION_TYPE_ONLINE_THERAPY_SLUG,
  STATIC_STATE_INSURANCE_LISTS
} from '@sondermind/data-access/directory-link';
import {
  IFilterFormOptionValue,
  IFilterFormValues,
  PSYCHIATRIST_URL_TITLE,
  SESSION_TYPES
} from '@sondermind/data-access/public-facing/filter-form';
import {
  BrowseLinkKeys,
  BrowseLinkKeyType,
  IAPIListResponse,
  IBrowseLinkApiResponse,
  IBrowseLinkData,
  IBrowseLinkWithUrl,
  IBrowseLinkSection
} from '@sondermind/utilities/models-http';

import { BrowseLinksStore } from '@sondermind/data-access/public-facing/browse-links';
import { StrapiTokenStore } from '@sondermind/third-party/strapi';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';

@Component({
  imports: [
    CommonModule,
    RouterModule,
  ],
  selector: 'browse-therapists-card',
  templateUrl: './browse-therapists-card.component.html',
  styleUrls: ['./browse-therapists-card.component.scss']
})
export class BrowseTherapistsCard implements OnInit, OnChanges, OnDestroy {
  @Input() currentStateAbbrev!: string;
  @Input() selectedCity?: string;
  @Input() currentFilters?: Partial<IFilterFormValues>;

  private loadDataTrigger$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  private sortedCities: IBrowseLinkData[] = [];
  private sortedInsurances: IBrowseLinkData[] = [];
  private sortedSpecialities: IBrowseLinkData[] = [];
  private sortedTreatmentPreferences: IBrowseLinkData[] = [];
  private readonly defaultItemCount = 5;

  providerDirectoryUrl: string = '';
  currentStateName: string = '';

  browseSections$ = new BehaviorSubject<IBrowseLinkSection[]>([]);
  showAll$ = new BehaviorSubject<boolean>(false);
  hasMore$ = new BehaviorSubject<boolean>(false);

  constructor(
    private browseLinksStore: BrowseLinksStore,
    private configurationService: ConfigurationService,
    private strapiTokenStore: StrapiTokenStore,
  ) {}

  ngOnInit(): void {
    this.providerDirectoryUrl =
    this.configurationService.env.providerDirectoryBase ||
    'https://www.sondermind.com/provider-directory';

    this.initializeDataLoader();
    this.initializeShowAllSubscription();

    if (this.currentStateAbbrev?.trim()) this.loadDataTrigger$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const stateChanged = changes['currentStateAbbrev']?.currentValue as string | undefined;
    const cityChanged = changes['selectedCity']?.currentValue as string | undefined;
    const filtersChanged = changes['currentFilters'];

    if (stateChanged) {
      this.currentStateAbbrev = stateChanged.trim();
      this.currentStateName = this.currentStateAbbrev
        ? USStateAbbrevMap[this.currentStateAbbrev] || this.currentStateAbbrev
        : '';
    }

    if (cityChanged) {
      this.selectedCity = cityChanged;
    }

    if (this.currentStateAbbrev) {
      if (filtersChanged && !stateChanged && !cityChanged) {
        this.rebuildSections();
      } else {
        this.loadDataTrigger$.next();
      }
    }
  }

  private initializeShowAllSubscription(): void {
    this.showAll$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.rebuildSections();
    });
  }

  private initializeDataLoader(): void {
    this.loadDataTrigger$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.getBrowseDataObservable())
      ).subscribe(({ stateData, cityData, sitemapCities }) => {
        const dataSource = cityData?.data ?? stateData.data;
        const apiCities = this.getTopItems(stateData.data, BrowseLinkKeys.CITY);
        const apiCityValues = new Set(apiCities.map((c) => c.value.toLowerCase()));
        this.sortedCities = [...apiCities, ...sitemapCities.filter((c) => !apiCityValues.has(c.value.toLowerCase()))];
        this.sortedInsurances = this.getTopItems(dataSource, BrowseLinkKeys.INSURANCE);
        this.sortedSpecialities = this.getTopItems(dataSource, BrowseLinkKeys.SPECIALTY);
        this.sortedTreatmentPreferences = this.getTopItems(dataSource, BrowseLinkKeys.TREATMENT_PREFERENCE);

        const hasMoreCities = this.sortedCities.length > this.defaultItemCount;
        const hasMoreInsurances = this.sortedInsurances.length > this.defaultItemCount;
        const hasMoreSpecialities = this.sortedSpecialities.length > this.defaultItemCount;
        const hasMoreTreatmentPreferences = this.sortedTreatmentPreferences.length > this.defaultItemCount;
        this.hasMore$.next(hasMoreCities || hasMoreInsurances || hasMoreSpecialities || hasMoreTreatmentPreferences);

        this.rebuildSections();
      });
  }

  private rebuildSections(): void {
    const shouldShowAll = this.showAll$.getValue();
    const itemCount = shouldShowAll ? undefined : this.defaultItemCount;

    const cities = itemCount ? this.sortedCities.slice(0, itemCount) : this.sortedCities;
    const insurances = itemCount ? this.sortedInsurances.slice(0, itemCount) : this.sortedInsurances;
    const specialities = itemCount ? this.sortedSpecialities.slice(0, itemCount) : this.sortedSpecialities;
    const treatmentPreferences = itemCount
      ? this.sortedTreatmentPreferences.slice(0, itemCount)
      : this.sortedTreatmentPreferences;

    const sections: IBrowseLinkSection[] = [];

    if (specialities.length) {
      sections.push({
        title: 'Search by Specialty',
        headingId: 'specialty-heading',
        ariaLabelPrefix: 'Search for',
        items: this.buildLinksWithUrls(specialities, URIPrefix.SPECIALTY, false)
      });
    }

    if (insurances.length) {
      sections.push({
        title: 'Search by Insurance',
        headingId: 'insurance-heading',
        ariaLabelPrefix: 'Search for providers accepting',
        items: this.buildLinksWithUrls(insurances, URIPrefix.INSURANCE, false)
      });
    }

    if (treatmentPreferences.length) {
      sections.push({
        title: 'Search by Treatment Approaches',
        headingId: 'treatment-heading',
        ariaLabelPrefix: 'Search for providers who offer',
        items: this.buildLinksWithUrls(treatmentPreferences, URIPrefix.TREATMENT_PREFERENCE, false)
      });
    }

    if (cities.length) {
      sections.push({
        title: 'Search by City',
        headingId: 'city-heading',
        ariaLabelPrefix: 'Search for providers in',
        items: this.buildLinksWithUrls(cities, URIPrefix.CITY, true)
      });
    }

    this.browseSections$.next(sections);

    // Update city and insurance tokens for Strapi content
    this.updateCityTokens();
    this.updateInsuranceTokens();
  }

  private buildLinksWithUrls(
    items: IBrowseLinkData[],
    prefix: string, isCityLink: boolean
  ): IBrowseLinkWithUrl[] {
    const baseUrl = `${this.providerDirectoryUrl}/${URIPrefix.STATE}-${this.currentStateAbbrev?.toLocaleLowerCase()}`;
    const uriSafeCity = this.getUriSafeCity();

    return items.map((item) => {
      const citySegment = isCityLink
        ? `${URIPrefix.CITY}-${item.uriSafeValue}`
        : `${URIPrefix.CITY}-${uriSafeCity || EmptyURIValue}`;

      const filterSegment = this.buildFilterSegment(
        isCityLink ? null : prefix,
        isCityLink ? null : item.uriSafeValue
      );

      return {
        ...item,
        url: `${baseUrl}/${citySegment}${filterSegment}`
      };
    });
  }

  private getUriSafeCity(): string {
    if (!this.selectedCity || this.selectedCity === EmptyURIValue) {
      return '';
    }
    return encodeURIComponent(encodeSpecialCharacters(this.selectedCity.toLocaleLowerCase()));
  }

  private buildFilterSegment(clickedPrefix: string | null, clickedValue: string | null): string {
    const isCityLink = clickedPrefix === null;
    const parts: string[] = [];
    const filters = this.currentFilters;

    if (!filters && !isCityLink) {
      return `/${clickedPrefix}-${clickedValue}`;
    }

    if (!filters) {
      return '';
    }

    // Order: insurance > modalities > specialties > genders > session_types > ages > treatment_preferences
    const modalityItems: IFilterFormOptionValue[] = [
      ...(filters.serviceType?.tag === PSYCHIATRIST_URL_TITLE ? [filters.serviceType] : []),
      ...(filters.modalities ?? [])
    ];

    this.addFilterParts(
      parts, URIPrefix.INSURANCE, clickedPrefix, clickedValue, filters.insurance?.title ? [filters.insurance.title] : []
    );
    this.addFilterParts(parts, URIPrefix.MODALITY, clickedPrefix, clickedValue, modalityItems);
    this.addFilterParts(parts, URIPrefix.SPECIALTY, clickedPrefix, clickedValue, filters.specialties);
    this.addFilterParts(parts, URIPrefix.GENDER, clickedPrefix, null, filters.genders);
    const sessionFormatValues = (filters.sessionFormats ?? []).map((format) => this.toDirectorySlug(format));
    this.addFilterParts(parts, URIPrefix.SESSION_TYPE, clickedPrefix, null, sessionFormatValues);
    this.addFilterParts(parts, URIPrefix.AGE, clickedPrefix, null, filters.ages);
    this.addFilterParts(
      parts, URIPrefix.TREATMENT_PREFERENCE, clickedPrefix, clickedValue, filters.treatmentPreferences
    );

    return parts.length ? `/${parts.join(UriDelimiter)}` : '';
  }

  /**
   * The online format's display title ('Video') is not the canonical directory slug. Emit the
   * indexed online-therapy slug so the link round-trips to the online/video filter via the
   * parser's LEGACY_SESSION_TYPE_URL_MAP. Other formats already match their directory slug by title.
   */
  private toDirectorySlug(format: IFilterFormOptionValue): string {
    return format.tag === SESSION_TYPES.online.tag ? SESSION_TYPE_ONLINE_THERAPY_SLUG : format.title;
  }

  private addFilterParts(
    parts: string[],
    prefix: string,
    clickedPrefix: string | null,
    clickedValue: string | null,
    items?: IFilterFormOptionValue[] | string[]
  ): void {
    if (clickedPrefix !== prefix && items?.length) {
      items.forEach((item) => {
        const value = typeof item === 'string' ? item : item.title;
        parts.push(`${prefix}-${encodeURIComponent(encodeSpecialCharacters(value.toLocaleLowerCase()))}`);
      });
    }
    if (clickedPrefix === prefix && clickedValue) {
      parts.push(`${prefix}-${clickedValue}`);
    }
  }

  private updateCityTokens(): void {
    const cityTokens: Record<string, string> = {};
    const filteredCities = this.sortedCities
      .filter((city) => city.value.toLowerCase() !== this.selectedCity?.toLowerCase());
    filteredCities.slice(0, 2).forEach((city, index) => {
      cityTokens[`city_${index + 1}`] = city.value;
    });
    this.strapiTokenStore.setTokens(cityTokens);
  }

  private updateInsuranceTokens(): void {
    const insuranceList = this.formatListWithAnd(
      this.sortedInsurances.slice(0, 3).map((i) => i.value)
    );

    if (!insuranceList) {
      this.strapiTokenStore.removeToken('insurance_list');
      return;
    }

    this.strapiTokenStore.setToken('insurance_list', insuranceList);
  }

  private formatListWithAnd(items: string[]): string | null {
    if (!items.length) return null;
    if (items.length === 1) return items[0];

    return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
  }

  private getBrowseDataObservable(): Observable<{
    stateData: IAPIListResponse<IBrowseLinkApiResponse>;
    cityData: IAPIListResponse<IBrowseLinkApiResponse> | null;
    sitemapCities: IBrowseLinkData[];
  }> {
    const stateLinks$ = this.browseLinksStore.getBrowseLinks$(this.currentStateAbbrev);
    const sitemapCities$ = this.browseLinksStore.getSitemapCities$(this.currentStateAbbrev);
    const uriSafeCity = this.getUriSafeCity();

    if (uriSafeCity && this.selectedCity) {
      const cityLinks$ = this.browseLinksStore.getBrowseCityLinks$(this.currentStateAbbrev, this.selectedCity);
      return forkJoin({ stateData: stateLinks$, cityData: cityLinks$, sitemapCities: sitemapCities$ });
    }

    return forkJoin({ stateData: stateLinks$, cityData: of(null), sitemapCities: sitemapCities$ });
  }

  private getTopItems(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    if (!items?.length) {
      return [];
    }

    if (key === BrowseLinkKeys.INSURANCE) {
      const stateAbbrev = this.currentStateAbbrev.toLowerCase();
      const stateInsurances = STATIC_STATE_INSURANCE_LISTS[stateAbbrev];
      if (stateInsurances) {
        return this.filterAndSortInsuranceItems(items, stateInsurances);
      }
    }

    return this.filterAndSortItems(items, key);
  }

  private filterAndSortInsuranceItems(
    items: IBrowseLinkApiResponse[],
    stateInsurances: string[]
  ): IBrowseLinkData[] {
    const validInsurances = new Set(stateInsurances.map((ins) => ins.toLowerCase()));

    const { validItems, remainingItems } = items.reduce<{
      validItems: IBrowseLinkApiResponse[];
      remainingItems: IBrowseLinkApiResponse[];
    }>((acc, item) => {
      const insurance = item.insurance?.toLowerCase();
      if (!insurance) return acc;

      if (validInsurances.has(insurance)) {
        acc.validItems.push(item);
      } else {
        acc.remainingItems.push(item);
      }
      return acc;
    }, { validItems: [], remainingItems: [] });

    const sortedValidItems = validItems.sort((a, b) => b.provider_count - a.provider_count);

    // Get remaining insurances sorted
    const remainingSortedItems = this.filterAndSortItems(
      remainingItems,
      BrowseLinkKeys.INSURANCE
    );

    return [
      ...this.mapToBrowseLinkData(sortedValidItems, BrowseLinkKeys.INSURANCE),
      ...remainingSortedItems
    ];
  }

  private filterAndSortItems(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    const keyField = key as keyof IBrowseLinkApiResponse;

    const sortedItems = items
      .filter((item) => item[keyField] != null)
      .sort((a, b) => {
        // First sort by provider_count in descending order
        const countDiff = b.provider_count - a.provider_count;
        if (countDiff !== 0) return countDiff;

        // If provider_count is equal, sort alphabetically by the key value
        const aValue = (a[keyField] ?? '').toString().toLowerCase();
        const bValue = (b[keyField] ?? '').toString().toLowerCase();
        return aValue.localeCompare(bValue);
      });

    return this.mapToBrowseLinkData(sortedItems, key);
  }

  private mapToBrowseLinkData(
    items: IBrowseLinkApiResponse[],
    key: BrowseLinkKeyType
  ): IBrowseLinkData[] {
    const keyTyped = key as keyof IBrowseLinkApiResponse;

    return items.map((item) => {
      const value = item[keyTyped] as string;
      return {
        value,
        uriSafeValue: encodeURIComponent(encodeSpecialCharacters(value.toLocaleLowerCase()))
      };
    });
  }

  toggleShowAll(): void {
    this.showAll$.next(!this.showAll$.getValue());
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
